/**
 * 独立剧本创作页 · DeepSeek 提示词 / 解析 / 本地草稿
 */
import { volcChatComplete } from '@/utils/nodeCanvasVolcChat'

export const SCRIPT_STUDIO_STORAGE_KEY = 'plus_script_studio_draft_v1'
export const SCRIPT_STUDIO_DEEPSEEK_MODEL = 'deepseek-v4-flash'
export const SCRIPT_STUDIO_DEEPSEEK_BASE = 'https://api.deepseek.com'

export const SCRIPT_STUDIO_SECTIONS = [
  { id: 'planning', label: '剧本策划', icon: 'fas fa-lightbulb' },
  { id: 'outline', label: '剧本大纲', icon: 'fas fa-list-ol' },
  { id: 'characters', label: '角色设定', icon: 'fas fa-users' },
  { id: 'world', label: '世界设定', icon: 'fas fa-globe' },
  { id: 'script', label: '剧本正文', icon: 'fas fa-file-alt' }
]

export const SCRIPT_STUDIO_MARKETS = [
  { id: 'cn', label: '国内' },
  { id: 'na', label: '北美' },
  { id: 'sea', label: '东南亚' },
  { id: 'global', label: '全球' }
]

export const SCRIPT_STUDIO_CATEGORIES = [
  { id: '女频', label: '女频' },
  { id: '男频', label: '男频' },
  { id: '通吃', label: '通吃' }
]

export function createEmptyScriptStudioDraft() {
  return {
    uuid: '',
    idea: '',
    aiTopic: {
      title: '',
      category: '',
      themes: [],
      oneLiner: '',
      audience: '',
      core: '',
      mainPlot: '',
      highlights: ''
    },
    plan: {
      title: '',
      theme: '',
      market: 'cn',
      category: '女频',
      genres: [],
      genreText: '',
      episodeCount: 40,
      episodeDurationMin: 1,
      episodeDurationMax: 3,
      maxCharacters: 8,
      maxScenesPerEpisode: 5,
      language: 'Chinese (Simplified)',
      maxWordsPerEpisode: 1500,
      dialogueDensity: '',
      styleFormat: '竖屏短剧',
      coreRequirements: ''
    },
    outline: '',
    characters: '',
    world: '',
    /** 分批正文：{ '1-5': '…', '6-10': '…' } */
    episodeBodies: {},
    scriptFrom: 1,
    scriptTo: 5,
    coverPrompt: '',
    coverUrl: '',
    updatedAt: null,
    cloudSavedAt: null
  }
}

export function loadScriptStudioDraft() {
  try {
    const raw = localStorage.getItem(SCRIPT_STUDIO_STORAGE_KEY)
    if (!raw) return createEmptyScriptStudioDraft()
    const data = JSON.parse(raw)
    if (!data || typeof data !== 'object') return createEmptyScriptStudioDraft()
    const empty = createEmptyScriptStudioDraft()
    return {
      ...empty,
      ...data,
      aiTopic: { ...empty.aiTopic, ...(data.aiTopic || {}) },
      plan: { ...empty.plan, ...(data.plan || {}) },
      episodeBodies:
        data.episodeBodies && typeof data.episodeBodies === 'object' ? data.episodeBodies : {}
    }
  } catch {
    return createEmptyScriptStudioDraft()
  }
}

export function saveScriptStudioDraft(draft) {
  try {
    const payload = {
      ...(draft || createEmptyScriptStudioDraft()),
      updatedAt: new Date().toISOString()
    }
    localStorage.setItem(SCRIPT_STUDIO_STORAGE_KEY, JSON.stringify(payload))
    return true
  } catch {
    return false
  }
}

function stripCodeFence(text) {
  let s = String(text || '').trim()
  if (s.startsWith('```')) {
    s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '')
  }
  return s.trim()
}

export function parseJsonFromModelText(text) {
  const s = stripCodeFence(text)
  try {
    return JSON.parse(s)
  } catch {
    const m = s.match(/\{[\s\S]*\}/)
    if (m) {
      try {
        return JSON.parse(m[0])
      } catch {
        return null
      }
    }
    return null
  }
}

function marketLabel(id) {
  const hit = SCRIPT_STUDIO_MARKETS.find(x => x.id === id)
  return hit ? hit.label : String(id || '国内')
}

export function buildPlanningContext(draft) {
  const d = draft || createEmptyScriptStudioDraft()
  const p = d.plan || {}
  const t = d.aiTopic || {}
  const genres =
    (Array.isArray(p.genres) && p.genres.length
      ? p.genres
      : String(p.genreText || '')
          .split(/[,，、/\s]+/)
          .map(x => x.trim())
          .filter(Boolean)
    ).join('、') ||
    (Array.isArray(t.themes) ? t.themes.join('、') : '')
  const lines = [
    d.idea ? `【灵感】${d.idea}` : '',
    p.title || t.title ? `【剧名】${p.title || t.title}` : '',
    p.theme || t.oneLiner ? `【主题/一句话】${p.theme || t.oneLiner}` : '',
    `【市场】${marketLabel(p.market)}`,
    `【种类】${p.category || t.category || ''}`,
    genres ? `【题材】${genres}` : '',
    p.episodeCount ? `【总集数】${p.episodeCount}` : '',
    p.episodeDurationMin || p.episodeDurationMax
      ? `【单集时长】${p.episodeDurationMin || '?'}–${p.episodeDurationMax || '?'} 分钟`
      : '',
    p.coreRequirements ? `【核心要求】${p.coreRequirements}` : '',
    t.mainPlot ? `【主线】${t.mainPlot}` : '',
    t.core ? `【内核】${t.core}` : '',
    t.highlights ? `【亮点】${t.highlights}` : ''
  ]
  return lines.filter(Boolean).join('\n')
}

async function deepseekChat(system, user, signal) {
  return volcChatComplete({
    model: SCRIPT_STUDIO_DEEPSEEK_MODEL,
    baseUrl: SCRIPT_STUDIO_DEEPSEEK_BASE,
    signal,
    messages: [
      { role: 'system', content: system },
      { role: 'user', content: user || ' ' }
    ]
  })
}

const PLANNING_SYSTEM = `你是资深短剧策划与 AI 选题专家。根据用户灵感与约束，输出一份可落地的短剧策划。
必须只输出一个 JSON 对象（不要 markdown 围栏、不要解释），结构如下：
{
  "aiTopic": {
    "title": "剧名",
    "category": "女频|男频|通吃",
    "themes": ["题材标签1","题材标签2"],
    "oneLiner": "一句话故事",
    "audience": "受众描述",
    "core": "内核主题",
    "mainPlot": "主线剧情（150-300字）",
    "highlights": "亮点（可含 AIGC 视效建议）"
  },
  "plan": {
    "title": "剧名",
    "theme": "主题/一句话",
    "market": "cn|na|sea|global",
    "category": "女频|男频|通吃",
    "genres": ["标签1","标签2"],
    "episodeCount": 40,
    "episodeDurationMin": 1,
    "episodeDurationMax": 3,
    "maxCharacters": 8,
    "maxScenesPerEpisode": 5,
    "language": "Chinese (Simplified)",
    "maxWordsPerEpisode": 1500,
    "dialogueDensity": "中",
    "styleFormat": "竖屏短剧",
    "coreRequirements": "核心创作要求"
  },
  "coverPrompt": "英文或中英双语电影海报级封面生成提示词，描述主角、氛围、主色"
}`

const OUTLINE_SYSTEM = `你是资深短剧编剧。根据已有策划，输出完整「剧本大纲」。
使用中文 Markdown，包含：
1. 系列 logline
2. 三幕结构（开端 / 中段 ident / 高潮反转）
3. 分集大纲：按总集数列出每集标题 + 2～4 句剧情 + 集末钩子
不要寒暄，直接输出大纲正文。`

const CHARACTERS_SYSTEM = `你是资深角色设计师与编剧。根据策划与大纲，输出「角色设定」。
使用中文 Markdown，每位主要角色包含：姓名、身份、年龄外貌、性格、动机、人物弧光、与他人关系、标志性动作/口头禅、AIGC 出图提示词（英文一行）。
至少 4 个角色（含反派）。不要寒暄。`

const WORLD_SYSTEM = `你是资深世界观架构师。根据策划与角色，输出「世界设定」。
使用中文 Markdown，包含：时代与地理、势力/阶层、规则与禁忌、关键场景清单（含氛围与 AIGC 场景提示词）、道具与符号、视觉色板。
不要寒暄。`

const SCRIPT_BODY_SYSTEM = `你是资深影视编剧。请根据策划、大纲、角色与世界设定，撰写可直接拍摄/分镜的中文文学剧本正文。

硬性要求：
- 只写用户指定的集数范围，每集单独成章，标题格式：「第N集 · 标题」
- 每集包含若干场景，场景头标注：场景X · 内/外 · 日/夜 · 地点
- 动作描写与对白分开；对白格式：角色名：台词
- 遵守单集时长与字数上限；开场 3 秒内冲突，集末必须有钩子
- 人物性格与设定一致，禁止寒暄与创作说明，直接输出剧本正文`

/** 按总集数切成 5 集一批的区间列表 */
export function buildEpisodeBatches(episodeCount) {
  const total = Math.max(1, Math.min(200, Number(episodeCount) || 40))
  const batches = []
  for (let start = 1; start <= total; start += 5) {
    const end = Math.min(total, start + 4)
    batches.push({
      id: `${start}-${end}`,
      label: `${start}-${end}集剧本正文`,
      from: start,
      to: end
    })
  }
  return batches
}

export function episodeRangeKey(from, to) {
  return `${Math.max(1, Number(from) || 1)}-${Math.max(1, Number(to) || 1)}`
}

function buildFullCreativeContext(draft) {
  const d = draft || createEmptyScriptStudioDraft()
  const parts = [buildPlanningContext(d)]
  if (d.outline) parts.push(`【剧本大纲】\n${d.outline}`)
  if (d.characters) parts.push(`【角色设定】\n${d.characters}`)
  if (d.world) parts.push(`【世界设定】\n${d.world}`)
  return parts.filter(Boolean).join('\n\n')
}

/**
 * AI 生成剧本策划（含 AI选题 + 表单字段 + 封面提示词）
 */
export async function generateScriptPlanning(draft, signal) {
  const d = draft || createEmptyScriptStudioDraft()
  const user = [
    '请基于以下信息完成短剧策划 JSON：',
    buildPlanningContext(d) || '（用户仅给了很少信息，请自行创意补全，但保持可拍短剧节奏）',
    d.idea ? '' : '用户未写灵感时，请创作一个有差异化卖点的原创短剧选题。'
  ]
    .filter(Boolean)
    .join('\n\n')

  const text = await deepseekChat(PLANNING_SYSTEM, user, signal)
  const json = parseJsonFromModelText(text)
  if (!json || typeof json !== 'object') {
    throw new Error('模型返回无法解析，请重试')
  }
  return json
}

export async function generateScriptOutline(draft, signal) {
  const ctx = buildPlanningContext(draft)
  if (!ctx) throw new Error('请先完成剧本策划，或填写灵感后再生成大纲')
  const extra = draft && draft.outline ? `\n\n【已有大纲草稿，请在此基础上完善】\n${draft.outline}` : ''
  return deepseekChat(OUTLINE_SYSTEM, ctx + extra, signal)
}

export async function generateScriptCharacters(draft, signal) {
  const ctx = buildPlanningContext(draft)
  const outline = draft && draft.outline ? `\n\n【剧本大纲】\n${draft.outline}` : ''
  if (!ctx && !outline) throw new Error('请先完成剧本策划或大纲')
  const extra =
    draft && draft.characters ? `\n\n【已有角色草稿，请完善】\n${draft.characters}` : ''
  return deepseekChat(CHARACTERS_SYSTEM, ctx + outline + extra, signal)
}

export async function generateScriptWorld(draft, signal) {
  const ctx = buildPlanningContext(draft)
  const chars = draft && draft.characters ? `\n\n【角色设定】\n${draft.characters}` : ''
  if (!ctx && !chars) throw new Error('请先完成剧本策划或角色设定')
  const extra = draft && draft.world ? `\n\n【已有世界草稿，请完善】\n${draft.world}` : ''
  return deepseekChat(WORLD_SYSTEM, ctx + chars + extra, signal)
}

/**
 * 生成指定集数范围的剧本正文
 * @param {object} draft
 * @param {{ from: number, to: number }} range
 * @param {AbortSignal} [signal]
 */
export async function generateScriptBody(draft, range, signal) {
  const d = draft || createEmptyScriptStudioDraft()
  const total = Math.max(1, Number(d.plan && d.plan.episodeCount) || 40)
  let from = Math.max(1, Number(range && range.from) || 1)
  let to = Math.max(from, Number(range && range.to) || from)
  to = Math.min(total, to)
  from = Math.min(from, to)

  const ctx = buildFullCreativeContext(d)
  if (!ctx) throw new Error('请先完成剧本策划（建议再生成大纲/角色后再写正文）')

  const p = d.plan || {}
  const key = episodeRangeKey(from, to)
  const prev = d.episodeBodies && d.episodeBodies[key] ? String(d.episodeBodies[key]) : ''
  const user = [
    ctx,
    `【本次任务】请撰写第 ${from} 集至第 ${to} 集的完整剧本正文（共 ${to - from + 1} 集）。`,
    `单集时长约 ${p.episodeDurationMin || 1}–${p.episodeDurationMax || 3} 分钟；每集字数上限约 ${p.maxWordsPerEpisode || 1500} 字；每集场景上限 ${p.maxScenesPerEpisode || 5} 个。`,
    p.styleFormat ? `风格格式：${p.styleFormat}` : '',
    p.dialogueDensity ? `对白密度：${p.dialogueDensity}` : '',
    prev ? `【已有该范围草稿，请在此基础上重写完善】\n${prev.slice(0, 6000)}` : ''
  ]
    .filter(Boolean)
    .join('\n\n')

  return deepseekChat(SCRIPT_BODY_SYSTEM, user, signal)
}

/** 写入某一集段正文 */
export function setEpisodeBody(draft, from, to, text) {
  const next = {
    ...createEmptyScriptStudioDraft(),
    ...(draft || {}),
    uuid: (draft && draft.uuid) || '',
    episodeBodies: { ...((draft && draft.episodeBodies) || {}) }
  }
  const key = episodeRangeKey(from, to)
  next.episodeBodies = { ...next.episodeBodies, [key]: String(text || '') }
  next.scriptFrom = Math.max(1, Number(from) || 1)
  next.scriptTo = Math.max(next.scriptFrom, Number(to) || next.scriptFrom)
  return next
}

export function getEpisodeBody(draft, from, to) {
  const key = episodeRangeKey(from, to)
  const bodies = (draft && draft.episodeBodies) || {}
  return String(bodies[key] || '')
}

/** 合并策划 JSON 到草稿 */
export function applyPlanningJsonToDraft(draft, json) {
  const prevUuid = draft && draft.uuid ? String(draft.uuid) : ''
  const next = {
    ...createEmptyScriptStudioDraft(),
    ...(draft || {}),
    uuid: prevUuid,
    aiTopic: { ...createEmptyScriptStudioDraft().aiTopic, ...((draft && draft.aiTopic) || {}) },
    plan: { ...createEmptyScriptStudioDraft().plan, ...((draft && draft.plan) || {}) },
    episodeBodies: { ...((draft && draft.episodeBodies) || {}) }
  }
  const topic = json.aiTopic && typeof json.aiTopic === 'object' ? json.aiTopic : {}
  const plan = json.plan && typeof json.plan === 'object' ? json.plan : {}

  next.aiTopic = {
    ...next.aiTopic,
    title: String(topic.title || next.aiTopic.title || '').trim(),
    category: String(topic.category || next.aiTopic.category || '').trim(),
    themes: Array.isArray(topic.themes)
      ? topic.themes.map(x => String(x).trim()).filter(Boolean)
      : next.aiTopic.themes,
    oneLiner: String(topic.oneLiner || next.aiTopic.oneLiner || '').trim(),
    audience: String(topic.audience || next.aiTopic.audience || '').trim(),
    core: String(topic.core || next.aiTopic.core || '').trim(),
    mainPlot: String(topic.mainPlot || next.aiTopic.mainPlot || '').trim(),
    highlights: String(topic.highlights || next.aiTopic.highlights || '').trim()
  }

  const genres = Array.isArray(plan.genres)
    ? plan.genres.map(x => String(x).trim()).filter(Boolean)
    : next.plan.genres
  next.plan = {
    ...next.plan,
    title: String(plan.title || next.aiTopic.title || next.plan.title || '').trim(),
    theme: String(plan.theme || next.aiTopic.oneLiner || next.plan.theme || '').trim(),
    market: ['cn', 'na', 'sea', 'global'].includes(String(plan.market))
      ? String(plan.market)
      : next.plan.market,
    category: String(plan.category || next.aiTopic.category || next.plan.category || '').trim(),
    genres,
    genreText: genres.join('、'),
    episodeCount: Number(plan.episodeCount) || next.plan.episodeCount,
    episodeDurationMin: Number(plan.episodeDurationMin) || next.plan.episodeDurationMin,
    episodeDurationMax: Number(plan.episodeDurationMax) || next.plan.episodeDurationMax,
    maxCharacters: Number(plan.maxCharacters) || next.plan.maxCharacters,
    maxScenesPerEpisode: Number(plan.maxScenesPerEpisode) || next.plan.maxScenesPerEpisode,
    language: String(plan.language || next.plan.language || '').trim(),
    maxWordsPerEpisode: Number(plan.maxWordsPerEpisode) || next.plan.maxWordsPerEpisode,
    dialogueDensity: String(plan.dialogueDensity || next.plan.dialogueDensity || '').trim(),
    styleFormat: String(plan.styleFormat || next.plan.styleFormat || '').trim(),
    coreRequirements: String(plan.coreRequirements || next.plan.coreRequirements || '').trim()
  }

  if (json.coverPrompt) {
    next.coverPrompt = String(json.coverPrompt).trim()
  }
  return next
}

/** 汇总全文，便于下载 / 送入画布 */
export function buildScriptStudioExportMarkdown(draft) {
  const d = draft || createEmptyScriptStudioDraft()
  const t = d.aiTopic || {}
  const p = d.plan || {}
  const themes = (Array.isArray(t.themes) && t.themes.length
    ? t.themes
    : Array.isArray(p.genres)
      ? p.genres
      : []
  ).join('、')
  return [
    `# ${p.title || t.title || '未命名剧本'}`,
    '',
    '## AI选题',
    `1. 剧名：${t.title || p.title || '-'}`,
    `2. 种类：${t.category || p.category || '-'}`,
    `3. 题材：${themes || '-'}`,
    `4. 一句话：${t.oneLiner || p.theme || '-'}`,
    `5. 受众：${t.audience || '-'}`,
    `6. 内核：${t.core || '-'}`,
    `7. 主线：${t.mainPlot || '-'}`,
    `8. 亮点：${t.highlights || '-'}`,
    '',
    '## 剧本策划',
    `- 市场：${marketLabel(p.market)}`,
    `- 总集数：${p.episodeCount}`,
    `- 单集时长：${p.episodeDurationMin}-${p.episodeDurationMax} 分钟`,
    `- 语言：${p.language}`,
    `- 核心要求：${p.coreRequirements || '-'}`,
    '',
    '## 剧本大纲',
    d.outline || '（未生成）',
    '',
    '## 角色设定',
    d.characters || '（未生成）',
    '',
    '## 世界设定',
    d.world || '（未生成）',
    '',
    '## 剧本正文',
    ...(() => {
      const bodies = d.episodeBodies && typeof d.episodeBodies === 'object' ? d.episodeBodies : {}
      const keys = Object.keys(bodies).sort((a, b) => {
        const na = parseInt(a, 10) || 0
        const nb = parseInt(b, 10) || 0
        return na - nb
      })
      if (!keys.length) return ['（未生成）']
      const blocks = []
      keys.forEach(k => {
        blocks.push(`### 第${k}集`, bodies[k] || '（空）', '')
      })
      return blocks
    })(),
    '',
    '## 封面提示词',
    d.coverPrompt || '（未生成）'
  ].join('\n')
}
